/**
 * Поле ввода с подписью и ошибкой.
 *
 * Текст ошибки говорит, что случилось и что делать: «Неверный логин или пароль»,
 * а не «Ошибка валидации». Подпись связана с полем через id, чтобы работал клик по ней.
 */

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Текст ошибки под полем; пусто — ошибки нет */
  error?: string;
  /** Пояснение под полем, когда ошибки нет */
  hint?: ReactNode;
}

export function Field({
  label,
  error,
  hint,
  className = '',
  ...rest
}: FieldProps): React.JSX.Element {
  const id = useId();
  const describedBy = error !== undefined ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error !== undefined}
        {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
        className={
          'h-11 rounded-lg border bg-white px-3 text-base text-slate-900 ' +
          'focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-blue-500 ' +
          (error !== undefined ? 'border-red-500 ' : 'border-slate-300 ') +
          className
        }
        {...rest}
      />
      {error !== undefined ? (
        <p id={`${id}-error`} className="text-sm text-red-700">
          {error}
        </p>
      ) : hint !== undefined ? (
        <p id={`${id}-hint`} className="text-sm text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
